import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { useState } from 'react';
import { useInbox, useMarkAllRead, useMarkRead, useUnreadCount } from '../../features/shell/hooks';

function ago(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

/** Stage 7 Phase 2A — the header bell over the caller's own in-app inbox. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount();
  const inbox = useInbox(open);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const count = unread.data?.unread ?? 0;

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        className="relative flex h-8 w-8 items-center justify-center rounded-tight text-ink-muted hover:bg-sunken"
      >
        <Icon name="notifications" className="text-[18px]" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-alert px-1 text-[10px] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-80 rounded-md border border-line bg-surface shadow-raised"
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-semibold text-ink">Notifications</span>
            <button
              type="button"
              disabled={count === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
              className="text-xs text-accent-fg disabled:text-ink-subtle"
            >
              Mark all read
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {inbox.isLoading && <p className="px-3 py-3 text-xs text-ink-subtle">Loading…</p>}
            {inbox.isError && <p className="px-3 py-3 text-xs text-alert">Could not load notifications.</p>}
            {inbox.data && inbox.data.data.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-ink-subtle">You&rsquo;re all caught up.</p>
            )}
            {inbox.data?.data.map((n) => (
              <DropdownMenu.Item
                key={n.inbox_id}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!n.read_at) markRead.mutate(n.inbox_id);
                }}
                className="flex cursor-pointer gap-2 border-b border-line/60 px-3 py-2 outline-none last:border-0 hover:bg-sunken"
              >
                <span
                  aria-hidden
                  className={'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ' + (n.read_at ? 'bg-transparent' : 'bg-accent')}
                />
                <span className="min-w-0">
                  <span className={'block truncate text-sm ' + (n.read_at ? 'text-ink-muted' : 'font-medium text-ink')}>
                    {n.title}
                  </span>
                  <span className="line-clamp-2 block text-xs text-ink-subtle">{n.body}</span>
                  <span className="mt-0.5 block text-[11px] text-ink-subtle">{ago(n.created_at)}</span>
                </span>
              </DropdownMenu.Item>
            ))}
          </div>
          <DropdownMenu.Item asChild>
            <Link to="/notifications" className="block border-t border-line px-3 py-2 text-center text-xs font-medium text-primary outline-none hover:bg-sunken">
              View all notifications
            </Link>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
